import { useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useObjectAccessModal } from '@/components/ObjectAccess/hooks';
import { useDeleteFilePrompt } from '@/components/Workspace.File/useDeleteFilePrompt';
import { useRenameFileModal } from '@/components/Workspace.File/useRenameFileModal';
import { MoveObjectModal } from '@/components/WorkspaceObject/MoveObjectModal';
import type { WorkspaceFile, WorkspaceObject } from '@/types';
import { getLocationFor } from '@trinity/chat-components';
import { useToggle } from '@trinity/chat-components';
import { ObjectAccessContainer } from '../ObjectAccess/ObjectAccessContainer';
import MoveObjectContainer from '../WorkspaceObject/MoveObjectContainer';
import type { FileActionsContextValue } from './Context';
import { DownloadFileContext, FileActionsContext } from './Context';
import { useFileMenuItems } from './hooks';
import { TranscribeFileContainer } from './TranscribeFileContainer';
import { EditTagsModal } from '@trinity/chat-components/components/EditTagsModal/EditTagsModal.js';
import GroupFileTagsContainer from '../GroupTags/GroupFileTagsContainer';

type Props =
  {
    file: WorkspaceFile;
    object: WorkspaceObject;
  } & ITranscriptId & ChildrenProps;

export const FileActionsContainer = (props: Props) => {

  const download = useContext(DownloadFileContext);
  const menu = useFileMenuItems();

  const [toggleDeletePrompt, DeleteFilePrompt] = useDeleteFilePrompt();
  const [toggleAccessModal, ManageAccessModal] = useObjectAccessModal();
  const [toggleRenameModal, RenameFileModal] = useRenameFileModal();
  const [moveObjectModalOpen, toggleMoveObjectModal] = useToggle();
  const [editTagsModalOpen, toggleEditTagsModal] = useToggle();

  const navigate = useNavigate();

  const viewFile = useCallback(() => {
    const location = getLocationFor.workspaces.file({
      fileId: props.file.id,
    });

    navigate(location);
  }, [navigate, props.file.id]);

  const actions: FileActionsContextValue = useMemo(() => ({
    changeOwner: () => { },
    delete: toggleDeletePrompt,
    download,
    downloadTranscript: () => { },
    manageAccess: toggleAccessModal,
    move: toggleMoveObjectModal,
    rename: toggleRenameModal,
    viewFile,
    manageTags: toggleEditTagsModal,
  }), [toggleDeletePrompt, download, toggleAccessModal, toggleMoveObjectModal, toggleRenameModal, toggleEditTagsModal, viewFile]);

  const fileId = props.file.id;
  const objectId = props.file.objectId;
  const workspaceId = props.object.workspaceId;

  return (
    <FileActionsContext.Provider value={actions}>
      <TranscribeFileContainer
        fileId={fileId}
        workspaceId={workspaceId}
        transcriptId={props.transcriptId || null}>
        {props.children}
        {menu.canDelete && (
          <DeleteFilePrompt
            fileId={fileId}
            workspaceId={workspaceId}
          />
        )}
        {menu.canRename && (
          <RenameFileModal
            fileId={fileId}
            workspaceId={workspaceId}
            name={props.file.name} />
        )}
        {menu.canManageAccess &&
          <ObjectAccessContainer objectId={objectId} workspaceId={workspaceId}>
            <ManageAccessModal />
          </ObjectAccessContainer>
        }
        {menu.canMove && (
          <MoveObjectContainer enabled={moveObjectModalOpen} objectId={objectId} objectType={props.object.typeId} workspaceId={workspaceId}>
            <MoveObjectModal open={moveObjectModalOpen} onClose={toggleMoveObjectModal} />
          </MoveObjectContainer>
        )}
        {editTagsModalOpen && (
          <GroupFileTagsContainer fileId={props.file.id}>
            <EditTagsModal open={editTagsModalOpen} onClose={toggleEditTagsModal} />
          </GroupFileTagsContainer>
        )}
      </TranscribeFileContainer>
    </FileActionsContext.Provider>
  );
};